import { SongSubmissionUsage } from "./library-objects";
import { Instrument, MidiMeasure, Part, PartList, Section, SectionJson, Tempo, TempoJson, TimeSignature } from "./music-objects";
import { BalancedEventBST } from "./project-event-balanced-tree";
import { User } from "./user-objects";
import { Utils } from "./utils";

// Project classes
export interface ProjectTrackItemJson {
	name: string;
	section: SectionJson;
	startMeasure: number;
	trimStart: number;
	trimEnd: number;
	originalSongId: string;
	originalPartName: string;
	originalSectionName: string;
}

export class ProjectTrackItem {
	private track: ProjectTrack | null = null;

	private readonly section: Section;
	private startMeasure: number;
	private trimStart: number;
	private trimEnd: number;
	private title: string;

	// Song usage tracking
	private readonly originalSongId: string;
	private readonly originalPartName: string;
	private readonly originalSectionName: string;

	public readonly id: string;
	public ghosted: boolean = false;

	private constructor(title: string, startMeasure: number, trimStart: number, trimEnd: number, section: Section, ghosted: boolean, originalSongId: string, originalPartName: string, originalSectionName: string, id?: string) {
		this.id = id ?? Utils.generateGUID();
		this.title = title;
		this.startMeasure = startMeasure;
		this.trimStart = trimStart;
		this.trimEnd = trimEnd;
		this.section = section;
		this.ghosted = ghosted;

		this.originalSongId = originalSongId;
		this.originalPartName = originalPartName;
		this.originalSectionName = originalSectionName;
	}

	public getHash(): string {
		return `${this.id}-${this.startMeasure}-${this.trimStart}-${this.trimEnd}`;
	}

	public static createNewGhostItem(title: string, partList: PartList, part: Part, section: Section, measure: number): ProjectTrackItem {
		const songId = partList.getSongId();
		if (songId === undefined) {
			throw new Error("Song ID not found for section.");
		}
		return new ProjectTrackItem(
			title,
			measure,
			0,
			0,
			section,
			true,
			songId,
			part.name,
			section.name
		);
	}

	public getTitle(): string {
		return this.title;
	}

	public setTitle(title: string): void {
		this.updateLastEditedDate();
		this.title = title;
	}

	public getSection(): Section {
		return this.section;
	}

	public getStartMeasure(): number {
		return this.startMeasure;
	}

	public setStartMeasure(startMeasure: number): void {
		this.updateLastEditedDate();
		this.startMeasure = startMeasure;
	}

	public getTrimmedStartMeasure(): number {
		return this.startMeasure + this.trimStart;
	}

	public getTrimmedLength(): number {
		return this.section.getLength() - this.trimStart - this.trimEnd;
	}

	public getTrimStart(): number {
		return this.trimStart;
	}

	public setTrimStart(trimStart: number): void {
		this.updateLastEditedDate();
		this.trimStart = trimStart;
	}

	public getTrimEnd(): number {
		return this.trimEnd;
	}

	public setTrimEnd(trimEnd: number): void {
		this.updateLastEditedDate();
		this.trimEnd = trimEnd;
	}

	public setProjectTrack(projectTrack: ProjectTrack | null): void {
		this.track = projectTrack;
	}

	public getProjectTrack(): ProjectTrack | null {
		return this.track;
	}

	public getAllNoteIdsInCommon(other: Set<string> | undefined): Set<string> {
		if (!other) {
			return new Set<string>();
		}

		let commonNoteIds = new Set<string>();
		// Getting the section's note ids for the trimmed section is O(n) where n is the number of notes in the section, which is the least efficient part of this function
		let sectionNotes = this.section.getNoteIdSet(this.trimStart, this.section.getLength() - this.trimEnd);
		for (let noteId of other) { // Iterating over the other set wil be faster because it is typically smaller or a subset of the section notes
			if (sectionNotes.has(noteId)) { // This is O(1) because it is a set
				commonNoteIds.add(noteId);
			}
		}
		return commonNoteIds;
	}

	private updateLastEditedDate(): void {
		const project = this.track?.getProject();
		if (!project) {
			return;
		}
		project.updateLastEditedDate();
	}

	public getInstrument(): Instrument {
		if (this.track === null) {
			throw new Error("ProjectTrack must be set before calling getInstrument on ProjectTrackItem.");
		}
		return this.track.getInstrument();
	}

	public toMidiMeasures(): MidiMeasure[] {
		return this.section.toMidiMeasures().slice(this.trimStart, this.section.getLength() - this.trimEnd);
	}

	public createCopy(keepId: boolean = false): ProjectTrackItem {
		return new ProjectTrackItem(
			this.title,
			this.getStartMeasure(),
			this.getTrimStart(),
			this.getTrimEnd(),
			this.getSection().createCopy(keepId),
			this.ghosted,
			this.originalSongId,
			this.originalPartName,
			this.originalSectionName,
			keepId ? this.id : undefined
		);
	}

	public makeReal(): void {
		this.ghosted = false;
	}

	public static fromJson(json: any): ProjectTrackItem {
		return new ProjectTrackItem(
			json.name,
			json.startMeasure,
			json.trimStart,
			json.trimEnd,
			Section.fromJson(json.section),
			false,
			json.originalSongId,
			json.originalPartName,
			json.originalSectionName,
			json.songUsageDetailId
		);
	}

	public toJson(): ProjectTrackItemJson {
		return {
			name: this.title,
			section: this.getSection().toJson(),
			startMeasure: this.getStartMeasure(),
			trimStart: this.getTrimStart(),
			trimEnd: this.getTrimEnd(),
			originalSongId: this.originalSongId,
			originalPartName: this.originalPartName,
			originalSectionName: this.originalSectionName
		};
	}
}

export interface ProjectTrackJson {
	instrumentName: string;
	items: ProjectTrackItemJson[];
}

export class ProjectTrack {
	private project: Project | null = null;
	private instrument: Instrument;

	public readonly id: string;
	public readonly items: ProjectTrackItem[];
	public ghosted: boolean;

	private constructor(instrument: Instrument, items: ProjectTrackItem[], ghosted: boolean, id?: string) {
		this.id = id ?? Utils.generateGUID();
		this.instrument = instrument;
		this.items = items;
		this.ghosted = ghosted;
		this.items.forEach((item: ProjectTrackItem) => item.setProjectTrack(this));
	}

	public getHash(): string {
		return `${this.id}-${this.instrument.name}-${this.items.map(item => item.getHash()).join('-')}`;
	}

	public static createGhostTrack(instrument: Instrument): ProjectTrack {
		const projectTrack = new ProjectTrack(
			instrument.createCopy(),
			[],
			true
		);
		return projectTrack;
	}

	public createCopy(removeGhostItems: boolean = false): ProjectTrack {
		const items = this.items.map((item: ProjectTrackItem) => item.createCopy(true)).filter((item: ProjectTrackItem) => !removeGhostItems || !item.ghosted);
		const projectTrack = new ProjectTrack(
			this.instrument.createCopy(),
			items,
			this.ghosted,
			this.id
		);
		items.forEach((item: ProjectTrackItem) => item.setProjectTrack(projectTrack));
		return projectTrack;
	}

	public makeGhostsReal(): ProjectTrackItem[] {
		const newItems: ProjectTrackItem[] = this.items.flatMap((item: ProjectTrackItem) => {
			if (item.ghosted) {
				item.makeReal();
				return [item];
			} else {
				return [];
			}
		});
		this.ghosted = false;
		return newItems;
	}

	public addItem(item: ProjectTrackItem): void {
		this.items.push(item);
		item.setProjectTrack(this);
	}

	public sortItems(): void {
		this.items.sort((a: ProjectTrackItem, b: ProjectTrackItem) => a.getTrimmedStartMeasure() - b.getTrimmedStartMeasure());
	}

	public removeItem(item: ProjectTrackItem): void {
		const index = this.items.findIndex((i: ProjectTrackItem) => i.id === item.id);
		if (index !== -1) {
			this.items.splice(index, 1);
		}
		item.setProjectTrack(null);
	}

	public getInstrument(): Instrument {
		return this.instrument;
	}

	public setInstrument(instrument: Instrument): void {
		this.updateLastEditedDate();
		this.instrument = instrument;
	}

	public setProject(project: Project | null): void {
		this.project = project;
	}

	public getProject(): Project | null {
		return this.project;
	}

	public toMidiMeasures(): MidiMeasure[] {
		// Create a list of all the midi measures in the track by combining all the midi measures of each item and empty measures for the gaps between items
		const midiMeasures: MidiMeasure[] = [];
		let currentMeasure = 0;
		let lastTimeSignature: TimeSignature | undefined;
		this.sortItems();
		this.items.forEach((item: ProjectTrackItem) => {
			const startMeasure = item.getStartMeasure() + item.getTrimStart();
			const sectionMidiMeasures = item.toMidiMeasures();
			lastTimeSignature = lastTimeSignature ?? sectionMidiMeasures[0].timeSignature;
			while (currentMeasure < startMeasure) {
				midiMeasures.push(new MidiMeasure(lastTimeSignature, []));
				currentMeasure++;
			}
			lastTimeSignature = sectionMidiMeasures[sectionMidiMeasures.length - 1].timeSignature;
			midiMeasures.push(...sectionMidiMeasures);
			currentMeasure += sectionMidiMeasures.length;
		});
		return midiMeasures;
	}

	private updateLastEditedDate(): void {
		const project = this.project;
		if (project === null) {
			return;
		}
		project.updateLastEditedDate();
	}

	public static fromJson(json: any): ProjectTrack {
		const instrument = Instrument.getInstrumentMatch(json.instrumentName);
		if (instrument === null) {
			throw new Error("Instrument not found for ProjectTrack.");
		}
		const track = new ProjectTrack(
			instrument,
			json.items.map((item: any) => ProjectTrackItem.fromJson(item)),
			false
		);
		return track;
	}

	public toJson(): ProjectTrackJson {
		return {
			instrumentName: this.instrument.name,
			items: this.items.map((item: ProjectTrackItem) => item.toJson())
		};
	}
}

interface ProjectEventJson {
	type: string;
	measureNumber: number;
}

class ProjectEventType {
	public static TEMPO_CHANGE: string = "tempoChange";
}

export abstract class ProjectEvent {
	public abstract type: string;

	public static fromJson(json: any): ProjectEvent {
		switch (json.type) {
			case ProjectEventType.TEMPO_CHANGE:
				return TempoChange.fromJson(json);
			default:
				throw new Error("Event type not recognized: " + json.type);
		}
	}

	public abstract createCopy(): ProjectEvent;

	public abstract toJson(measureNumber: number): ProjectEventJson;
}

interface TempoChangeJson extends ProjectEventJson {
	tempo: TempoJson;
}

export class TempoChange extends ProjectEvent {
	public type: string = ProjectEventType.TEMPO_CHANGE;
	public tempo: Tempo;

	constructor(tempo: Tempo) {
		super();
		this.tempo = tempo;
	}

	public createCopy(): TempoChange {
		return new TempoChange(new Tempo(this.tempo.getBpm()));
	}

	public static fromJson(tempoJson: any): TempoChange {
		return new TempoChange(Tempo.fromJson(tempoJson));
	}

	public toJson(measureNumber: number): TempoChangeJson {
		return {
			type: this.type,
			tempo: this.tempo.toJson(),
			measureNumber
		};
	}
}

interface ProjectDataJson {
	tracks: ProjectTrackJson[];
	tempoChanges: TempoChangeJson[];
}

interface ProjectJson {
	id: string;
	name: string;
	projectData: ProjectDataJson;
	created: Date;
	lastEdited: Date;
}

interface RestorableProjectJson {
	name: string;
	created: Date;
	lastEdited: Date;
	projectData: ProjectDataJson;
	userId: string;
}

export class Project {
	private created: Date;
	private lastEdited: Date;
	private isNew: boolean;
	private locked: boolean;
	private name: string;
	private id: string;
	private user: User;
	private dirty: boolean;

	public static readonly FIRST_MEASURE: number = 0;
	public static maxMeasures: number = 100;

	public readonly tracks: ProjectTrack[];
	public readonly submissions: ProjectSubmission[];
	// We use a balanced binary search tree to store tempo changes by measure number so we can quickly find the tempo at any measure
	private tempoChanges: BalancedEventBST<TempoChange> = new BalancedEventBST();

	private constructor(
		id: string,
		name: string,
		user: User,
		created: Date,
		lastEdited: Date,
		submissions: ProjectSubmission[],
		locked: boolean,
		isNew: boolean,
		dirty: boolean
	) {
		this.id = id ?? Utils.generateGUID();
		this.name = name;
		this.user = user;
		this.created = created;
		this.lastEdited = lastEdited;
		this.submissions = submissions;
		this.locked = locked;
		this.tracks = [];
		this.isNew = isNew;
		this.dirty = dirty;
	}

	public getId(): string {
		return this.id;
	}

	public getUser(): User | undefined {
		return this.user;
	}

	public isNewProject(): boolean {
		return this.isNew;
	}

	public userCanEdit(otherUser: User): boolean {
		if (otherUser === null) {
			return false;
		}

		if (otherUser === undefined) {
			return false;
		}
		if (this.user === undefined) {
			this.user = otherUser;
		}
		return otherUser.roles.includes("ConductionAdmin") || otherUser.id === this.user.id;
	}

	public isDirty() {
		return this.dirty;
	}

	public setDirty(dirty: boolean) {
		this.dirty = dirty;
	}

	public loadTracks(newTracks: ProjectTrack[]): void {
		newTracks.forEach((newTrack: ProjectTrack) => this.addTrack(newTrack));
		this.removeEmptyTracks();
	}

	public loadTempoChanges(newTempoChangeEvents: { measureNumber: number, tempoChange: TempoChange }[]): void {
		newTempoChangeEvents.forEach(tce => this.tempoChanges.insert(tce.measureNumber, tce.tempoChange));

		// Ensure there is a tempo change at the start of the project to set the starting tempo
		if (!this.tempoChanges.getLatest(Project.FIRST_MEASURE)) {
			this.tempoChanges.insert(Project.FIRST_MEASURE, new TempoChange(Tempo.DEFAULT_TEMPO));
		}
	}

	public sortTracks(): void {
		this.tracks.sort((a: ProjectTrack, b: ProjectTrack) => b.getInstrument().midiProgram - a.getInstrument().midiProgram);
	}

	public static async createNew(user: User): Promise<Project> {
		if (user == null) {
			throw new Error("You can only create new projects after logging in.");
		}

		return new Project(
			Utils.generateGUID(),
			"",
			user,
			new Date(),
			new Date(),
			[],
			false,
			true,
			true
		);
	}

	public isSameProject(project: Project): boolean {
		return this.id === project.id;
	}

	public createCopy(removeGhostTracksAndItems: boolean = false): Project {
		const copy = new Project(
			this.id,
			this.name,
			this.user,
			this.created,
			this.lastEdited,
			this.submissions,
			this.locked,
			this.isNew,
			this.dirty
		);
		const tracks = this.tracks.map((track: ProjectTrack) => track.createCopy(removeGhostTracksAndItems)).filter((track: ProjectTrack) => !removeGhostTracksAndItems || !track.ghosted);
		copy.loadTracks(tracks);
		copy.tempoChanges = this.tempoChanges.createCopy();
		return copy;
	}

	public hasItems(): boolean {
		return this.tracks.some((track: ProjectTrack) => track.items.length > 0);
	}

	public setName(newName: string): void {
		const sanitizedName = newName
			.replace(/[^a-zA-Z0-9!@#\$%\^\&*\)\(+=._\- ]+/g, '') // Allow spaces
			.replace(/\s+/g, ' '); // Replace multiple spaces with a single space
		this.name = sanitizedName;
	}

	public getName(): string {
		return this.name;
	}

	public isLocked(): boolean {
		return this.locked;
	}

	public lock(): void {
		this.locked = true;
	}

	public getCreatedDate(): Date {
		return this.created;
	}

	public updateLastEditedDate(): void {
		this.lastEdited = new Date();
	}

	public getLastEditedDate(): Date {
		return this.lastEdited;
	}

	public addTempoChange(measureNumber: number, newTempo: Tempo): void {
		this.tempoChanges.insert(measureNumber, new TempoChange(newTempo));
	}

	public getLatestTempo(measureNumber: number): Tempo {
		let tempo: Tempo = this.tempoChanges.getLatest(measureNumber)?.tempo ?? Tempo.DEFAULT_TEMPO;
		return tempo;
	}

	public makeGhostsReal(): ProjectTrackItem[] {
		const newItems: ProjectTrackItem[] = this.tracks.flatMap((track: ProjectTrack) => track.makeGhostsReal());
		return newItems;
	}

	public addTrack(projectTrack: ProjectTrack): void {
		this.tracks.push(projectTrack);
		projectTrack.setProject(this);
		this.sortTracks();
	}

	public getItems(): ProjectTrackItem[] {
		return this.tracks.flatMap((track: ProjectTrack) => track.items);
	}

	public chopItem(itemId: string, measure: number): ProjectTrackItem[] {
		const affectedItems = [];
		const items = this.tracks.flatMap((track: ProjectTrack) => track.items);
		const itemToChop = items.find((item: ProjectTrackItem) => item.id === itemId && measure > item.getStartMeasure() + item.getTrimStart() && measure < item.getStartMeasure() + item.getSection().getLength() - item.getTrimEnd());
		if (itemToChop != null) {
			const track = itemToChop.getProjectTrack();
			if (track === null) {
				throw new Error("ProjectTrack must be set before calling chopItem on ProjectTrackItem.");
			}

			const trimEnd = itemToChop.getStartMeasure() + itemToChop.getSection().getLength() - measure; // Set the trim right to land the item at the measure
			const newItemLeft = itemToChop.createCopy();
			newItemLeft.setTrimEnd(trimEnd < 0 ? 0 : trimEnd); // Ensure trim right is not negative

			const trimStart = measure - itemToChop.getStartMeasure();
			const newItemRight = itemToChop.createCopy();
			newItemRight.setTrimStart(trimStart); // Set the trim right to match the original item's length

			track.removeItem(itemToChop);
			track.addItem(newItemLeft);
			track.addItem(newItemRight);
			affectedItems.push(newItemLeft);
			affectedItems.push(newItemRight);
		}
		return affectedItems;
	}

	private willItemsOverlapWithNewStart(newItem1Start: number, item1: ProjectTrackItem, item2: ProjectTrackItem): number {
		if (item1.getProjectTrack() !== item2.getProjectTrack()) {
			throw new Error("Items must be on the same track to call willItemsOverlapWithNewStart.");
		}

		if ((newItem1Start + item1.getTrimStart() < item2.getStartMeasure() + item2.getSection().getLength() - item2.getTrimEnd() && newItem1Start + item1.getSection().getLength() - item1.getTrimEnd() > item2.getStartMeasure() + item2.getTrimStart())
			|| (newItem1Start + item1.getTrimStart() >= item2.getStartMeasure() + item2.getTrimStart() && newItem1Start + item1.getSection().getLength() - item1.getTrimEnd() <= item2.getStartMeasure() + item2.getSection().getLength() - item2.getTrimEnd()) // first contains second
			|| (item2.getStartMeasure() + item2.getTrimStart() >= newItem1Start + item1.getTrimStart() && item2.getStartMeasure() + item2.getSection().getLength() - item2.getTrimEnd() <= newItem1Start + item1.getSection().getLength() - item1.getTrimEnd()) // second contains first
		) {
			const item1Center = newItem1Start + item1.getTrimStart() + (item1.getSection().getLength() - item1.getTrimEnd()) / 2.0;
			const item2Center = item2.getStartMeasure() + item2.getTrimStart() + (item2.getSection().getLength() - item2.getTrimEnd()) / 2.0;
			if (item1Center < item2Center) {
				// item1 is to the left of item2
				return -1;
			} else {
				// item1 is to the right of item2
				return 1;
			}
		} else {
			return 0;
		}
	}

	private willItemsOverlapWithNewTrimStart(newItem1TrimStart: number, item1: ProjectTrackItem, item2: ProjectTrackItem): boolean {
		if (item1.getProjectTrack() !== item2.getProjectTrack()) {
			throw new Error("Items must be on the same track to call willItemsOverlapWithNewTrimStart.");
		}

		if (item1.getStartMeasure() + newItem1TrimStart < item2.getStartMeasure() + item2.getSection().getLength() - item2.getTrimEnd() && item1.getStartMeasure() + item1.getSection().getLength() - item1.getTrimEnd() > item2.getStartMeasure() + item2.getTrimStart()) {
			return true;
		} else {
			return false;
		}
	}

	private willItemsOverlapWithNewTrimEnd(newItem1TrimEnd: number, item1: ProjectTrackItem, item2: ProjectTrackItem): boolean {
		if (item1.getProjectTrack() !== item2.getProjectTrack()) {
			throw new Error("Items must be on the same track to call willItemsOverlapWithNewTrimEnd.");
		}

		if (item1.getStartMeasure() + item1.getTrimStart() < item2.getStartMeasure() + item2.getSection().getLength() - item2.getTrimEnd() && item1.getStartMeasure() + item1.getSection().getLength() - newItem1TrimEnd > item2.getStartMeasure() + item2.getTrimStart()) {
			return true;
		} else {
			return false;
		}
	}

	public moveItems(itemIds: string[], itemActionLeader: ProjectTrackItem, measure: number): boolean {
		const itemActionLeaderStartMeasure = itemActionLeader.getStartMeasure();
		// Adjust the measure if the any of the items will go out of bounds or overlap with other items
		var checksComplete = false, impossibleMove = false;
		var direction = 0; // this variable makes sure we don't get stuck in an infinite loop by forcing only one direction for jumps
		while (!checksComplete && !impossibleMove) {
			checksComplete = true;
			this.tracks.forEach((track) => {
				if (impossibleMove) return;
				const unselectedItems = track.items.filter((item) => !itemIds.includes(item.id));
				track.items.forEach((item) => {
					if (impossibleMove) return;
					if (itemIds.includes(item.id)) {
						const startMeasureDifference = item.getStartMeasure() - itemActionLeaderStartMeasure;
						var newStartMeasure = measure + startMeasureDifference;

						unselectedItems.forEach((unselectedItem) => {
							const itemsOverlap = this.willItemsOverlapWithNewStart(newStartMeasure, item, unselectedItem);
							if (itemsOverlap != 0 && direction == 1 || itemsOverlap > 0) {
								// this item is to the right of the unselected item
								newStartMeasure = unselectedItem.getStartMeasure() + unselectedItem.getSection().getLength() - unselectedItem.getTrimEnd() - item.getTrimStart();
								direction = 1;
								checksComplete = false;
							} else if (itemsOverlap != 0 && direction == -1 || itemsOverlap < 0) {
								// this item is to the left of the unselected item
								newStartMeasure = unselectedItem.getStartMeasure() + unselectedItem.getTrimStart() + item.getTrimEnd() - item.getSection().getLength();
								direction = -1;
								checksComplete = false;
							}
						});

						if (newStartMeasure + item.getSection().getLength() - item.getTrimEnd() > Project.maxMeasures) {
							// Don't allow items to be dropped outside of the timeline
							newStartMeasure = Project.maxMeasures - item.getSection().getLength() + item.getTrimEnd();
							checksComplete = false;
							if (direction != 0) {
								impossibleMove = true;
							}
						}
						if (newStartMeasure + item.getTrimStart() < 0) {
							newStartMeasure = 0 - item.getTrimStart();
							checksComplete = false;
							if (direction != 0) {
								impossibleMove = true;
							}
						}
						measure = newStartMeasure - startMeasureDifference;
					}
				});
			});
		}

		if (impossibleMove) {
			return false;
		}

		// Update the startMeasure of the items in the appropriate tracks
		this.getItems().forEach((item) => {
			if (itemIds.includes(item.id)) {
				const startMeasureDifference = item.getStartMeasure() - itemActionLeaderStartMeasure;
				const newStartMeasure = measure + startMeasureDifference;
				item.setStartMeasure(newStartMeasure);
			}
			return item;
		});
		return true;
	}

	public trimLeftItems(itemIds: string[], itemActionLeader: ProjectTrackItem, targetMeasure: number): boolean {
		const itemActionLeaderStartMeasure = itemActionLeader.getStartMeasure();
		const itemActionLeaderTrimStart = itemActionLeader.getTrimStart();
		const maxItemActionLeaderTrimStart = itemActionLeader.getSection().getLength();
		const measureDifference = targetMeasure - (itemActionLeaderStartMeasure + itemActionLeaderTrimStart);
		if (itemActionLeaderTrimStart + measureDifference < 0 || itemActionLeaderTrimStart + measureDifference >= maxItemActionLeaderTrimStart) {
			return false;
		}

		// Update the trimStart of the items in the appropriate tracks
		this.getItems().forEach((item) => {
			const track = item.getProjectTrack();
			if (track === null) {
				throw new Error("ProjectTrack must be set before calling trimLeftItems on ProjectTrackItem.");
			}

			if (itemIds.includes(item.id)) {
				var trimStart = item.getTrimStart() + measureDifference;
				trimStart = trimStart < 0 ? 0 : trimStart;
				trimStart = trimStart + item.getTrimEnd() + 1 > item.getSection().getLength() ? item.getSection().getLength() - item.getTrimEnd() - 1 : trimStart;

				var impossibleMove = item.getStartMeasure() + trimStart < 0;
				console.log("impossibleMove", impossibleMove, item.getStartMeasure(), trimStart, impossibleMove);
				track.items.forEach((timelineItem) => {
					if (impossibleMove || timelineItem.id == item.id) return;
					const itemsOverlap = this.willItemsOverlapWithNewTrimStart(trimStart, item, timelineItem);
					if (itemsOverlap) {
						impossibleMove = true;
						return;
					}
				});
				console.log(impossibleMove)
				if (!impossibleMove) {
					item.setTrimStart(trimStart);
				}
			}
			return item;
		});
		return true;
	}

	public trimRightItems(itemIds: string[], itemActionLeader: ProjectTrackItem, targetMeasure: number): boolean {
		const itemActionLeaderStartMeasure = itemActionLeader.getStartMeasure();
		const itemActionLeaderTrimEndDiff = itemActionLeader.getSection().getLength() - itemActionLeader.getTrimEnd();
		const measureDifference = targetMeasure - (itemActionLeaderStartMeasure + itemActionLeaderTrimEndDiff);
		if (itemActionLeader.getTrimEnd() - measureDifference < 0 || itemActionLeader.getTrimEnd() - measureDifference >= itemActionLeader.getSection().getLength()) {
			return false;
		}

		// Update the trimEnd of the items in the appropriate tracks
		this.getItems().forEach((item) => {
			const track = item.getProjectTrack();
			if (track === null) {
				throw new Error("ProjectTrack must be set before calling trimLeftItems on ProjectTrackItem.");
			}

			if (itemIds.includes(item.id)) {
				var trimEnd = item.getTrimEnd() - measureDifference;
				trimEnd = trimEnd < 0 ? 0 : trimEnd;
				trimEnd = trimEnd + item.getTrimStart() + 1 > item.getSection().getLength() ? item.getSection().getLength() - item.getTrimStart() - 1 : trimEnd;

				var impossibleMove = item.getStartMeasure() + item.getSection().getLength() - item.getTrimEnd() > Project.maxMeasures;
				track.items.forEach((timelineItem) => {
					if (impossibleMove || timelineItem.id == item.id) return;
					const itemsOverlap = this.willItemsOverlapWithNewTrimEnd(trimEnd, item, timelineItem);
					if (itemsOverlap) {
						impossibleMove = true;
						return;
					}
				});

				if (!impossibleMove) {
					item.setTrimEnd(trimEnd);
				}
			}
			return item;
		});
		return true;
	}

	public duplicateItems(items: ProjectTrackItem[], itemIdToRelativeMeasure: Map<string, number>, measure: number): ProjectTrackItem[] {
		var checksComplete = false, impossibleMove = false;
		while (!checksComplete && !impossibleMove) {
			checksComplete = true;
			this.tracks.forEach((track) => {
				if (impossibleMove) return;
				var timelineItems = track.items;
				items.forEach((item) => {
					if (impossibleMove) return;
					const relativeMeasure = itemIdToRelativeMeasure.get(item.id);
					if (relativeMeasure === undefined) return;
					var startMeasure = measure + relativeMeasure;
					if (track.items.includes(item)) {
						timelineItems.forEach((timelineItem) => {
							if (item.id !== timelineItem.id) {
								const itemsOverlap = this.willItemsOverlapWithNewStart(startMeasure - item.getTrimStart(), item, timelineItem);
								if (itemsOverlap != 0) {
									startMeasure = timelineItem.getStartMeasure() + timelineItem.getSection().getLength() - timelineItem.getTrimEnd();
									checksComplete = false;
								}
							}
						});
					}

					if (startMeasure + item.getSection().getLength() - item.getTrimEnd() > Project.maxMeasures) {
						// Don't allow items to be dropped outside of the timeline
						checksComplete = false;
						impossibleMove = true;
					}
					measure = startMeasure - relativeMeasure;
				});
			});
		}
		if (impossibleMove) {
			return [];
		}

		const newItems: ProjectTrackItem[] = [];
		items.forEach((item) => {
			let track = this.tracks.find((track) => track.items.includes(item));
			if (!track) return;
			const relativeMeasure = itemIdToRelativeMeasure.get(item.id);
			if (relativeMeasure === undefined) return;

			const newItem = item.createCopy();
			newItem.setStartMeasure(measure + relativeMeasure - item.getTrimStart());
			track.addItem(newItem);

			newItems.push(newItem);
		});

		return newItems
	}

	// only makes sense on items that are on the same track
	private willNewItemOverlap(newItemStart: number, newItemLength: number, item: ProjectTrackItem): number {
		const itemEnd = item.getStartMeasure() + item.getSection().getLength() - item.getTrimEnd();
		const itemStart = item.getStartMeasure() + item.getTrimStart();
		if (newItemStart < itemEnd && newItemStart + newItemLength > itemStart) {
			const existentItemCenter = item.getStartMeasure() + item.getTrimStart() + (item.getSection().getLength() - item.getTrimEnd()) / 2.0 + 1;
			if (newItemStart > existentItemCenter) {
				// new item is to the right of existent item
				return 1;
			} else {
				// new item is to the left of existent item
				return -1;
			}
		} else {
			return 0;
		}
	}

	public createGhostTrack(instrument: Instrument): ProjectTrack {
		const track = ProjectTrack.createGhostTrack(instrument);
		this.addTrack(track);
		return track;
	}

	public createGhostItemForSection(section: Section, measure: number): ProjectTrackItem {
		const sectionPart = section.getPart();
		if (sectionPart === null) {
			throw new Error("Section must be associated with a part to create an item from it.");
		}

		const sectionPartInstrument = sectionPart.instrument;
		if (sectionPartInstrument === null) {
			throw new Error("Section part must be associated with an instrument to create an item from it.");
		}

		const partList = sectionPart.getPartList();
		if (partList === null) {
			throw new Error("Section must be associated with a partlist to create an item from it.");
		}

		// See if there is an existing project track that matches the section part's instrument
		let track = this.tracks.find((track: ProjectTrack) => track.getInstrument().name === sectionPartInstrument.name);

		// If no matching project track is found, create a ghost track
		if (track === undefined) {
			track = this.createGhostTrack(sectionPartInstrument);
		}

		let itemTitle = section.name;
		const partName = sectionPart.name;
		if (partName) {
			itemTitle = `${itemTitle}: ${partName}`;
		}
		const partListName = sectionPart.getPartList()?.getName();
		if (partListName) {
			itemTitle = `${partListName} - ${itemTitle}`;
		}

		const item = ProjectTrackItem.createNewGhostItem(
			itemTitle,
			partList,
			sectionPart,
			section.createCopy(false),
			measure
		);

		track.addItem(item);
		return item;
	}

	public createNewItems(measure: number, sections: Section[]) {
		if (sections.length === 0) {
			throw new Error("Sections must be provided to create items from them.");
		}

		measure = Math.max(0, measure); // Ensure the measure is not negative

		const sectionPart = sections[0].getPart();
		if (sectionPart === null) {
			throw new Error("Section must be associated with a part to create an item from it.");
		}

		// Create a list of sections that are selected paired with their relative start measure
		const selectedSectionNames = sections.map((section) => section.name);
		const relativeStartMeasuresBySectionName = new Map<string, number>();
		let startMeasure = 0;
		for (let i = 0; i < sectionPart.sections.length; i++) {
			const sectionAppears = selectedSectionNames.includes(sectionPart.sections[i].name);
			if (sectionAppears) {
				relativeStartMeasuresBySectionName.set(sectionPart.sections[i].name, startMeasure);
				startMeasure += sectionPart.sections[i].getLength();
			}
		}

		const newItems: ProjectTrackItem[] = sections.map((section) => {
			const sectionRelativeStartMeasure = relativeStartMeasuresBySectionName.get(section.name);
			if (sectionRelativeStartMeasure === undefined) {
				throw new Error("Relative start measure must be set for each section.");
			}
			return this.createGhostItemForSection(section, sectionRelativeStartMeasure); // TODO: handle overlapping items due to simultaneous sections being added to the same track based on their instrument
		});

		// Adjust the measure if the any of the items will go out of bounds or overlap with other items
		const findSpotResult = this.findSpot(measure, newItems);
		measure = findSpotResult.measure;
		if (findSpotResult.impossibleMove) {
			const findSpotSecondTryResult = this.findSpot(measure, newItems, -findSpotResult.direction);
			measure = findSpotSecondTryResult.measure;
			if (findSpotSecondTryResult.impossibleMove) {
				return;
			}
		}

		// Update the startMeasure of the items in the appropriate tracks
		newItems.forEach((item) => {
			const newStartMeasure = measure + item.getStartMeasure();
			item.setStartMeasure(newStartMeasure);
		});
	}

	private findSpot(measure: number, newItems: ProjectTrackItem[], direction: number = 0): { measure: number, impossibleMove: boolean, direction: number } {
		var checksComplete = false, impossibleMove = false;
		while (!checksComplete && !impossibleMove) {
			checksComplete = true;
			this.tracks.forEach((track) => {
				if (impossibleMove) return;
				const nonGhostedItems = track.items.filter((item) => !item.ghosted);
				newItems.forEach((newItem) => {
					if (impossibleMove) return;
					var startMeasure = measure + newItem.getStartMeasure(), length = newItem.getSection().getLength();
					if (newItem.getProjectTrack() == track) {
						nonGhostedItems.forEach((nonGhostedItem) => {
							const itemsOverlap = this.willNewItemOverlap(startMeasure, length, nonGhostedItem);
							if (itemsOverlap != 0 && direction == 1 || itemsOverlap > 0) {
								// this item is to the right of the unselected item
								startMeasure = nonGhostedItem.getStartMeasure() + nonGhostedItem.getSection().getLength() - nonGhostedItem.getTrimEnd();
								direction = 1;
								checksComplete = false;
							} else if (itemsOverlap != 0 && direction == -1 || itemsOverlap < 0) {
								// this item is to the left of the unselected item
								startMeasure = nonGhostedItem.getStartMeasure() + nonGhostedItem.getTrimStart() - length;
								direction = -1;
								checksComplete = false;
							}
						});
					}

					if (startMeasure + length > Project.maxMeasures) {
						// Don't allow items to be dropped outside of the timeline
						startMeasure = Project.maxMeasures - length;
						checksComplete = false;
						if (direction != 0) {
							impossibleMove = true;
						}
					}
					if (startMeasure < 0) {
						startMeasure = 0;
						checksComplete = false;
						if (direction != 0) {
							impossibleMove = true;
						}
					}
					measure = startMeasure - newItem.getStartMeasure();
				});
			});
		}
		return {
			measure: measure,
			impossibleMove: impossibleMove,
			direction: direction
		}
	}

	public deleteItems(itemIds: string[]): void {
		this.getItems().forEach((item) => {
			if (itemIds.includes(item.id)) {
				const track = item.getProjectTrack();
				if (track === null) {
					throw new Error("ProjectTrack must be set before calling deleteItems on ProjectTrackItem.");
				}
				track.removeItem(item);
			}
		});

		this.removeEmptyTracks();
	}

	public removeEmptyTracks(): void {
		// Iterate backwards to avoid index issues when removing tracks
		for (let i = this.tracks.length - 1; i >= 0; i--) {
			if (this.tracks[i].items.length === 0) {
				this.tracks.splice(i, 1);
			}
		}
	}

	public static restoreFromJson(json: RestorableProjectJson, user: User): Project | null {
		if (user.id !== json.userId) { // Check if the user is the owner of the project
			return null;
		}

		const project = new Project(
			"",
			json.name,
			user,
			json.created,
			json.lastEdited,
			[],
			false,
			true,
			true
		);

		const projectData = json.projectData;
		if (projectData) {
			const tracks = projectData.tracks.map((track: any) => ProjectTrack.fromJson(track));
			project.loadTracks(tracks);
			project.tracks.forEach((track) => track.sortItems());

			const tempoChangeEvents = projectData.tempoChanges?.map((tempoChangeData: { measureNumber: number, tempo: any }) => {
				return {
					measureNumber: tempoChangeData.measureNumber,
					tempoChange: TempoChange.fromJson(tempoChangeData.tempo)
				}
			}) ?? [];
			project.loadTempoChanges(tempoChangeEvents);
		}

		project.setDirty(true);
		return project;
	}

	public static fromJson(json: any): Project {
		const project = new Project(
			json.id,
			json.name,
			User.fromJson(json.user),
			json.created,
			json.lastEdited,
			json.submissions.map((projectSubmission: any) => ProjectSubmission.fromJson(projectSubmission)),
			json.locked,
			false,
			false
		);

		const projectData = json.data;
		if (projectData) {
			const projectDataJson = JSON.parse(projectData);
			const tracks = projectDataJson.tracks.map((track: any) => ProjectTrack.fromJson(track));
			project.loadTracks(tracks);
			project.tracks.forEach((track) => track.sortItems());

			const tempoChangeEvents = projectDataJson.tempoChanges?.map((tempoChangeData: { measureNumber: number, tempo: any }) => {
				return {
					measureNumber: tempoChangeData.measureNumber,
					tempoChange: TempoChange.fromJson(tempoChangeData.tempo)
				}
			}) ?? [];
			project.loadTempoChanges(tempoChangeEvents);
		}

		return project;
	}

	public toRestorableJson(): RestorableProjectJson {
		return {
			name: this.name,
			projectData: {
				tracks: this.tracks.map((track: ProjectTrack) => track.toJson()),
				tempoChanges: this.tempoChanges.toJson()
			},
			created: this.created,
			lastEdited: this.lastEdited,
			userId: this.user.id
		};
	}

	public toJson(): ProjectJson {
		return {
			id: this.id,
			name: this.name,
			projectData: {
				tracks: this.tracks.map((track: ProjectTrack) => track.toJson()),
				tempoChanges: this.tempoChanges.toJson()
			},
			created: this.created,
			lastEdited: this.lastEdited
		};
	}
}

export class ProjectSubmission {
	public readonly id: string;
	public readonly dateSubmitted: Date;
	public readonly dateFulfilled: Date | null;
	public readonly feedbackDateFulfilled: Date | null;
	public readonly editorDueDate: Date;
	public readonly instrumentalistDueDate: Date;
	public readonly projectId: string;
	public readonly videoEditor: User | null;
	public readonly feedbackProvider: User | null;
	public readonly projectOwner: User | null;
	public readonly submissionProjectName: string | null;
	public readonly SongSubmissionUsage: SongSubmissionUsage[];
	public readonly instrumenatlistTasks: InstrumentalistTask[];

	private constructor(
		id: string,
		dateSubmitted: Date,
		dateFulfilled: Date | null,
		feedbackDateFulfilled: Date | null,
		editorDueDate: Date,
		instrumentalistDueDate: Date,
		projectId: string,
		videoEditor: User | null,
		feedbackProvider: User | null,
		projectOwner: User | null,
		submissionProjectName: string | null = null,
		songSubmissionUsage: SongSubmissionUsage[] = [],
		instrumenatlistTasks: InstrumentalistTask[]
	) {
		this.id = id;
		this.dateSubmitted = dateSubmitted;
		this.dateFulfilled = dateFulfilled;
		this.feedbackDateFulfilled = feedbackDateFulfilled;
		this.editorDueDate = editorDueDate;
		this.instrumentalistDueDate = instrumentalistDueDate;
		this.projectId = projectId;
		this.videoEditor = videoEditor ?? null;
		this.feedbackProvider = feedbackProvider ?? null;
		this.projectOwner = projectOwner;
		this.submissionProjectName = submissionProjectName ?? null;
		this.SongSubmissionUsage = songSubmissionUsage;
		this.instrumenatlistTasks = instrumenatlistTasks;
	}

	public static fromJson(json: any): ProjectSubmission {
		return new ProjectSubmission(
			json.projectSubmissionId,
			json.dateSubmitted,
			json.dateFulfilled,
			json.feedbackDateFulfilled,
			json.editorDueDate,
			json.instrumentalistDueDate,
			json.projectId,
			json.editor ? User.fromJson(json.editor) : null,
			json.feedbackProvider ? User.fromJson(json.feedbackProvider) : null,
			json.projectOwner ? User.fromJson(json.projectOwner) : null,
			json.submissionProjectName ?? null,
			json.songSubmissionUsage,
			json.instrumentalistTasks ? json.instrumentalistTasks.map((task: any) => InstrumentalistTask.fromJson(task)) : []
		);
	}
}

export class InstrumentalistTask {
	public readonly id: string;
	public readonly instrument: Instrument;
	public readonly instrumentalist: User;
	public readonly dateFulfilled: Date | null;

	private constructor(id: string, instrument: Instrument, instrumentalist: User, dateFulfilled: Date | null) {
		this.id = id;
		this.instrument = instrument;
		this.instrumentalist = instrumentalist;
		this.dateFulfilled = dateFulfilled;
	}

	public static fromJson(json: any): InstrumentalistTask {
		return new InstrumentalistTask(
			json.instrumentalistTaskId,
			Instrument.fromJson(json.instrument),
			User.fromJson(json.instrumentalist),
			json.dateFulfilled ? new Date(json.dateFulfilled) : null
		);
	}
}